import type { OperationsFlowRowDto, OperationsLensKey, OperationsPenKey, OperationsSummaryDto } from "@/api/endpoints";
import { InfoTooltip } from "@/components/InfoTooltip";
import { JOURNEY_ROW_TIP, TYPED_ROW_TIP, lensLabel, workTypeColor, workTypeLabel } from "../operationsLabels";
import { LensTag, UnlensedTag } from "./LensTags";

interface Props {
  flow: OperationsSummaryDto["flow"];
  periodLabel: string;
  attentionCount: number;
  /** The lens the open-now column was cut with, if any; each row then also shows its full-population count ("86 of 371"). */
  lens?: OperationsLensKey;
  /** The second lens (who holds the pen, 2026-10-28), cut the same way. */
  pen?: OperationsPenKey;
  lensedOpenNow: number;
  /** Stuck items whose cause is the waiting work order (no task pending, unsubmitted or unapproved past the threshold). */
  stuckByWorkOrder: number;
  onDrill: (workType: string | undefined, cohort: "open" | "started" | "ended") => void;
}

const FLOW_TIP =
  "Each row counts journeys: a consultant's intake, pre-verification and request are one. " +
  "Started = first record created in the period. Ended = work order approved (Onboarding, Redeployment) or request completed (other rows). " +
  "Cancelled work is left out.";
const OPEN_NOW_TIP =
  "Open journeys as of the snapshot, including those still at intake. Requests older than 200 days are outside the window.";

/** Panel 2: started against ended per work type, with Open now and Net. */
export function FlowTable({ flow, periodLabel, attentionCount, lens, pen, lensedOpenNow, stuckByWorkOrder, onDrill }: Props) {
  const label = lensLabel(lens, pen);
  const max = Math.max(1, ...flow.rows.flatMap((r) => [r.started, r.ended]));
  const pct = (v: number) => `${((v / max) * 82).toFixed(1)}%`; // leave room for the trailing label
  const net = (n: number) => (n > 0 ? `+${n}` : String(n));
  const unlensed = label ? " ops-unlensed" : "";
  const of = (r: OperationsFlowRowDto) => (label ? <span className="of"> of {r.openNowAll}</span> : null);

  return (
    <section className="ops-panel ops-span-8" aria-labelledby="ops-flow-h">
      <h3 id="ops-flow-h">
        Flow, {periodLabel}
        <InfoTooltip inline text={FLOW_TIP} />
        <LensTag lens={lens} pen={pen} />
        <span className="ops-legend">
          <span><i className="ops-sw" style={{ background: "var(--gw-blue-500)" }} />Started</span>
          <span><i className="ops-sw" style={{ background: "var(--gw-blue-300)" }} />Completed</span>
        </span>
      </h3>
      <table className="ops-flow">
        <thead>
          <tr>
            <th>Work type</th>
            <th className="numeric">
              Open now
              <InfoTooltip inline text={OPEN_NOW_TIP} />
            </th>
            <th className={unlensed.trim()}>Started, ended{label && <UnlensedTag />}</th>
            <th className={`numeric${unlensed}`}>Net</th>
          </tr>
        </thead>
        <tbody>
          {flow.rows.map((r: OperationsFlowRowDto) => {
            const consultant = r.workType === "Onboarding" || r.workType === "Redeployment";
            const noun = "journeys";
            return (
              <tr key={r.workType} data-flow-row="journey">
                <td className="wt">
                  <i className="ops-wt-dot" style={{ background: workTypeColor(r.workType) }} />
                  {workTypeLabel(r.workType)}
                  <InfoTooltip inline text={consultant ? JOURNEY_ROW_TIP : TYPED_ROW_TIP} />
                </td>
                <td className="open numeric">
                  <button type="button" className="ops-link-num" onClick={() => onDrill(r.workType, "open")} title={label ? `List the ${label} open ${noun}` : `List the open ${noun}`}>
                    {r.openNow}
                  </button>
                  {of(r)}
                </td>
                <td className={`bars${unlensed}`}>
                  <div className="ops-bar2">
                    <button type="button" className="b started" style={{ width: pct(r.started) }} onClick={() => onDrill(r.workType, "started")} title={`List the ${noun} started in the period`}>
                      <span>{r.started} started</span>
                    </button>
                    <button type="button" className="b ended" style={{ width: pct(r.ended) }} onClick={() => onDrill(r.workType, "ended")} title={consultant ? "List the journeys whose work order was approved in the period" : "List the journeys whose request completed in the period"}>
                      <span>{r.ended} ended</span>
                    </button>
                  </div>
                </td>
                <td className={`net numeric${unlensed}`}>{net(r.net)}</td>
              </tr>
            );
          })}
          <tr className="total">
            <td className="wt">All types</td>
            <td className="open numeric">{flow.total.openNow}{of(flow.total)}</td>
            <td className={`bars${unlensed}`}>
              {flow.total.started} started, {flow.total.ended} ended · {flow.total.openNowAll} open now, of which{" "}
              {label ? (
                <><b>{lensedOpenNow} are {label}</b> (lens)</>
              ) : (
                <>
                  {attentionCount} need attention
                  {stuckByWorkOrder > 0 && (
                    <span className="muted" title="Open items with no task pending whose work order has waited on our side (to submit or to approve) for more than the threshold: they count as Stuck.">
                      {" "}({stuckByWorkOrder} waiting on the work order)
                    </span>
                  )}
                </>
              )}
            </td>
            <td className={`net numeric${unlensed}`}>{net(flow.total.net)}</td>
          </tr>
        </tbody>
      </table>
      {flow.note && <p className="ops-flow-note" role="status">{flow.note}</p>}
    </section>
  );
}
